import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getOutboxNotifications } from "../api/outboxNotifications.api";
import type {
  OutboxNotificationFilters,
  OutboxNotificationItem,
  OutboxNotificationPagination,
  OutboxNotificationSummary,
} from "../types/outboxNotifications.types";

export const OUTBOX_NOTIFICATIONS_PAGE_SIZE = 20;

const emptyPagination: OutboxNotificationPagination = {
  page: 1,
  pageSize: OUTBOX_NOTIFICATIONS_PAGE_SIZE,
  totalCount: 0,
  totalPages: 0,
};

export function useOutboxNotifications() {
  const [items, setItems] = useState<OutboxNotificationItem[]>([]);
  const [summary, setSummary] = useState<OutboxNotificationSummary[]>([]);
  const [pagination, setPagination] = useState<OutboxNotificationPagination>(emptyPagination);
  const [page, setPage] = useState(1);
  const [filters] = useState<OutboxNotificationFilters>({
    sortBy: "updatedAt",
    sortDirection: "desc",
  });
  const [error, setError] = useState<string | null>(null);
  const { run, loading } = useLatestRequest();

  const refetch = useCallback(async () => {
    setError(null);
    try {
      const response = await run((signal) =>
        getOutboxNotifications({ ...filters, page, pageSize: OUTBOX_NOTIFICATIONS_PAGE_SIZE }, signal),
      );
      if (!response) return;

      setItems(response.items);
      setSummary(response.summary);
      setPagination(response.pagination);
    } catch (requestError: unknown) {
      setItems([]);
      setSummary([]);
      setPagination(emptyPagination);
      setError(getApiErrorMessage(requestError, "Failed to load Outbox notifications"));
    }
  }, [filters, page, run]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { items, summary, pagination, filters, page, setPage, error, loading, refetch };
}
